/* Screen-size pass, second part (14 October 2026).
   1 · 900 to 1,339 px: the header search icon opens the search field as a dropdown (the predictive search already
       listens to that form), instead of leaving for the search page. Escape, a click outside, the icon again or moving
       focus away close it. A click with Cmd, Ctrl, Shift or Alt still opens the search page.
   2 · phones and tablets: body.ftr-in while the footer fills the lower part of the screen after the page has been
       scrolled, so the wolf (Ask the Pack) steps aside there. Short pages, where the footer shows on arrival, keep it.
   3 · phones (under 700 px): a jobsite clip plays in its own tile, with sound and the phone's own controls, instead of
       opening the video box. Starting one pauses the others; swiping it off the screen pauses it; at the end the tile
       shows its cover again. Wider screens and YouTube/Vimeo clips keep the video box (nocry-global.js). (14 October)
   4 · every width: body.proof-under while the proof strip (four proofs: warranty, ratings, returns, the pack) or a
       hero's proof line (the Trustpilot line at the foot of the home page's hero, and of hero-page and lander heroes)
       passes the bottom corner of the screen, so the wolf steps aside instead of covering a proof. On a desktop the
       strip sits on the first screen of the home page, and on a phone the hero's Trustpilot line sits at its foot, so
       at every width the wolf shows only once the visit has scrolled on. (15 October; the hero line, 29 October) */
import { scrollMode } from '@theme/utils';

const mq = window.matchMedia('(min-width:900px) and (max-width:1339px)');

function searchDropdown() {
  const hdr = document.querySelector('.hdr');
  const icon = hdr && hdr.querySelector('.icons .s-m');
  const form = hdr && hdr.querySelector('form.hsearch');
  if (!icon || !form || hdr.__searchDrop) return;
  hdr.__searchDrop = true;
  const input = form.querySelector('input[name="q"]');
  if (!form.id) form.id = 'hsearch-form';
  const isOpen = () => hdr.classList.contains('search-open');
  const quiet = () => document.body.classList.contains('lander') || document.body.classList.contains('quiet');
  const active = () => mq.matches && !quiet();
  const inside = (el) => !!el && (form.contains(el) || icon.contains(el));
  function close(refocus) {
    if (!isOpen()) return;
    hdr.classList.remove('search-open'); icon.setAttribute('aria-expanded', 'false');
    const box = form.querySelector('.pres'); if (box) box.hidden = true;
    if (input) input.setAttribute('aria-expanded', 'false');
    if (refocus) icon.focus();
  }
  const open = () => { hdr.classList.add('search-open'); icon.setAttribute('aria-expanded', 'true'); if (input) input.focus(); };
  const setup = () => {
    if (active()) { icon.setAttribute('role', 'button'); icon.setAttribute('aria-controls', form.id); icon.setAttribute('aria-expanded', isOpen() ? 'true' : 'false'); }
    else { close(false); icon.removeAttribute('role'); icon.removeAttribute('aria-expanded'); icon.removeAttribute('aria-controls'); }
  };
  icon.addEventListener('click', (e) => {
    if (!active() || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
    e.preventDefault();
    if (isOpen()) close(true); else open();
  });
  icon.addEventListener('keydown', (e) => { if (e.key === ' ' && active()) { e.preventDefault(); icon.click(); } });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) close(true); });
  document.addEventListener('click', (e) => { if (isOpen() && !inside(e.target)) close(false); });
  /* focus moving to something outside the field and the icon (Tab, Shift+Tab). A click elsewhere is left to the click
     handler above: some browsers (Safari) move focus to the page on mousedown before the click on a suggestion lands. */
  const onFocusOut = (e) => { if (isOpen() && e.relatedTarget && !inside(e.relatedTarget)) close(false); };
  form.addEventListener('focusout', onFocusOut);
  icon.addEventListener('focusout', onFocusOut);
  if (mq.addEventListener) mq.addEventListener('change', setup); else if (mq.addListener) mq.addListener(setup);
  setup();
}

function footerWatch() {
  const ftr = document.querySelector('footer.ftr');
  if (!ftr || ftr.__watch || !('IntersectionObserver' in window)) return;
  ftr.__watch = true;
  new IntersectionObserver((es) => {
    document.body.classList.toggle('ftr-in', es[0].isIntersecting && window.scrollY > 200);
  }, { rootMargin: '0px 0px -25% 0px' }).observe(ftr);
}

const phone = window.matchMedia('(max-width:699px)');
const inlineTile = (el) => el && el.closest && el.closest('.ugc [data-video-src]');

function inlineClips() {
  if (document.__inlineClips) return;
  document.__inlineClips = true;
  /* a clip pauses once its tile has left the screen entirely (swiped along the row, or the page scrolled on) */
  const seen = ('IntersectionObserver' in window) ? new IntersectionObserver((es) => {
    es.forEach((en) => {
      if (en.isIntersecting) { en.target.__inView = true; return; }
      const v = en.target.querySelector('video.inl');
      const full = v && (document.fullscreenElement === v || v.webkitDisplayingFullscreen);
      if (v && !v.paused && en.target.__inView && !full) v.pause();
      en.target.__inView = false;
    });
  }) : null;
  const pauseOthers = (keep) => { document.querySelectorAll('.ugc video.inl').forEach((v) => { if (v !== keep && !v.paused) v.pause(); }); };
  const reset = (tile) => {
    const v = tile.querySelector('video.inl'); if (v) { v.pause(); v.remove(); }
    tile.classList.remove('playing');
    tile.setAttribute('role', 'button'); tile.setAttribute('tabindex', '0');
  };
  const play = (tile) => {
    let v = tile.querySelector('video.inl');
    if (!v) {
      v = document.createElement('video');
      v.className = 'inl'; v.src = tile.dataset.videoSrc; v.controls = true; v.playsInline = true; v.preload = 'auto';
      v.setAttribute('playsinline', ''); if (tile.dataset.videoTitle) v.setAttribute('aria-label', tile.dataset.videoTitle);
      v.addEventListener('play', () => pauseOthers(v));
      v.addEventListener('ended', () => { const had = tile.contains(document.activeElement); reset(tile); if (had) tile.focus({ preventScroll: true }); });
      tile.appendChild(v);
      if (seen) seen.observe(tile);
    }
    /* the tile stops being a button while its player is showing: the player's own controls take over */
    tile.classList.add('playing'); tile.removeAttribute('role'); tile.setAttribute('tabindex', '-1');
    /* a tile tapped while it only peeks in from the side slides fully into view */
    tile.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: scrollMode() });
    pauseOthers(v);
    const p = v.play(); if (p && p.catch) p.catch(() => {});
    return v;
  };
  /* runs before nocry-global.js's handler (capture), so the video box does not open for these */
  document.addEventListener('click', (e) => {
    const tile = inlineTile(e.target); if (!tile) return;
    /* taps and keys on a player already in a tile are the player's own, at any width */
    if (e.target.closest('video.inl')) { e.stopPropagation(); return; }
    if (!phone.matches) return;
    e.preventDefault(); e.stopPropagation();
    play(tile);
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const tile = inlineTile(e.target); if (!tile) return;
    if (e.target.closest('video.inl')) { e.stopPropagation(); return; }
    if (!phone.matches) return;
    e.preventDefault(); e.stopPropagation();
    const v = play(tile);
    /* focus moves to the player once the key is released: the player itself would read that key-up as pause */
    if (v) document.addEventListener('keyup', () => v.focus({ preventScroll: true }), { once: true, capture: true });
  }, true);
  /* a player stays in its tile if the phone is turned sideways (or goes full screen) and the page gets wider */
}

function proofWatch() {
  const strips = [...document.querySelectorAll('.proof, .hero .proofline')];
  if (!strips.length || document.__proofWatch) return;
  document.__proofWatch = true;
  let queued = false;
  const check = () => {
    queued = false;
    const h = window.innerHeight;
    /* the wolf's corner: the lowest 96 px of the screen */
    const under = strips.some((el) => { const r = el.getBoundingClientRect(); return r.height > 0 && r.top < h - 4 && r.bottom > h - 96; });
    document.body.classList.toggle('proof-under', under);
  };
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(check); } };
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  check();
}

function init() { searchDropdown(); footerWatch(); inlineClips(); proofWatch(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
document.addEventListener('shopify:section:load', init);
